import React from 'react';
import { ShieldCheck, WarningOctagon } from 'phosphor-react';
import { Card, CardContent } from '@/components/ui/card';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsMarginCard = ({ accountData, loading }) => {
  const { isPortuguese } = useOptionsLocale();

  // 1. Estado de Carregamento (Skeleton)
  if (loading) {
    return (
      <Card 
        className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[20px] text-white shadow-none p-4 space-y-3"
        data-testid="options-margin-card-loading"
      >
        <div className="flex justify-between items-center">
          <div className="h-3 w-16 bg-white/5 animate-pulse rounded" />
          <div className="h-3 w-8 bg-white/5 animate-pulse rounded" />
        </div>
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex justify-between">
              <div className="h-2.5 w-10 bg-white/5 animate-pulse rounded" />
              <div className="h-2.5 w-8 bg-white/5 animate-pulse rounded" />
            </div>
            <div className="h-1.5 w-full bg-white/5 animate-pulse rounded" />
          </div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <div className="h-2.5 w-10 bg-white/5 animate-pulse rounded" />
              <div className="h-2.5 w-8 bg-white/5 animate-pulse rounded" />
            </div>
            <div className="h-1.5 w-full bg-white/5 animate-pulse rounded" />
          </div>
        </div>
      </Card>
    );
  }

  // 2. Sem conexão Deribit
  if (!accountData || !accountData.connected) {
    return (
      <Card 
        className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[20px] text-white shadow-none p-4 flex flex-col justify-center items-center min-h-[110px] space-y-1 text-center"
        data-testid="options-margin-card-disconnected"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">MARGIN</span>
        <div className="flex items-center gap-1.5 text-xs text-white/40">
          <WarningOctagon size={14} />
          <span className="font-bold">Connect Deribit</span>
        </div>
      </Card>
    );
  }

  // 3. Estado de Erro de Sincronização
  if (accountData?.sync_status === 'failed') {
    return (
      <Card 
        className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[20px] text-white shadow-none p-4 flex flex-col justify-center items-center min-h-[110px] space-y-1 text-center"
        data-testid="options-margin-card-error"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">MARGIN</span>
        <div className="flex items-center gap-1.5 text-xs text-red-400">
          <WarningOctagon size={14} />
          <span className="font-semibold">Margin unavailable</span>
        </div>
      </Card>
    );
  }

  const currencies = accountData?.account_equity?.currencies || [];
  
  // Procurar o primeiro item válido de margem
  const activeCurrencyData = currencies.find(curr => {
    const balance = curr.margin_balance_native ?? curr.margin_balance ?? 0;
    const initialMargin = curr.initial_margin_native ?? curr.initial_margin ?? 0;
    const maintenanceMargin = curr.maintenance_margin_native ?? curr.maintenance_margin ?? 0;
    return balance > 0 && initialMargin >= 0 && maintenanceMargin >= 0;
  });

  // 4. Sem dados de margem válidos
  if (!activeCurrencyData) {
    return (
      <Card 
        className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[20px] text-white shadow-none p-4 flex flex-col justify-center items-center min-h-[110px] space-y-1 text-center"
        data-testid="options-margin-card-empty"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">MARGIN</span>
        <span className="text-lg font-black text-white/30 tracking-widest">—</span>
      </Card>
    );
  }

  const marginBalance = activeCurrencyData.margin_balance_native ?? activeCurrencyData.margin_balance ?? 0;
  const initialMargin = activeCurrencyData.initial_margin_native ?? activeCurrencyData.initial_margin ?? 0;
  const maintenanceMargin = activeCurrencyData.maintenance_margin_native ?? activeCurrencyData.maintenance_margin ?? 0;

  const imPercent = marginBalance > 0 ? (initialMargin / marginBalance) * 100 : 0;
  const mmPercent = marginBalance > 0 ? (maintenanceMargin / marginBalance) * 100 : 0;

  const imFormatted = imPercent.toFixed(2);
  const mmFormatted = mmPercent.toFixed(2);

  return (
    <Card 
      className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[20px] text-white shadow-none transition-all duration-300 hover:border-white/10"
      data-testid="options-margin-card"
    >
      <CardContent className="p-4 flex flex-col justify-between space-y-3">
        
        {/* Card Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45" data-testid="options-margin-label">
            <ShieldCheck size={12} weight="bold" />
            <span>MARGIN</span>
          </div>
        </div>

        {/* Single Compact Display */}
        <div className="space-y-3">

          {/* IM Display */}
          <div className="space-y-1" data-testid="options-margin-im">
            <div className="flex justify-between text-[11px] font-semibold text-white/50">
              <span>IM</span>
              <span className="font-mono font-bold text-white/85">
                {imFormatted}%
              </span>
            </div>
            <div className="relative h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-[#00E676] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(imPercent, 100)}%` }}
              />
            </div>
          </div>

          {/* MM Display */}
          <div className="space-y-1" data-testid="options-margin-mm">
            <div className="flex justify-between text-[11px] font-semibold text-white/50">
              <span>MM</span>
              <span className="font-mono font-bold text-white/85">
                {mmFormatted}%
              </span>
            </div>
            <div className="relative h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-[#A855F7] rounded-full transition-all duration-500"
                style={{ width: `${Math.min(mmPercent, 100)}%` }}
              />
            </div>
          </div>

        </div>

      </CardContent>
    </Card>
  );
};

export default OptionsMarginCard;
